import React, { useMemo, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronUp, FiCheckCircle, FiXCircle, FiCopy, FiEdit2, FiCheck, FiX, FiAlertTriangle, FiEye, FiEyeOff } from "react-icons/fi";
import { toast } from "react-toastify";
import styles from "./SubscriptionDrawerPanel.module.scss";
import OwnerTeamPanelWithData from "./OwnerTeamPanelWithData";
import CabinesSection from "./CabinesSection";
import Axios from "_services/caller.service";
import NavContext from "context/NavContext";
import { useTeamDataStandalone } from "components/Team/userCard/UseTeamDataStandalone";
import { SUBSCRIPTION_STATUSES, getStatusLabel, getStatusOptions } from "utils/subscriptionStatuses";

function SubscriptionDrawerPanel({ user, sub, onUpdateSubCabines, onRefresh }) {
  const { t } = useTranslation();
  const { userAuth } = useContext(NavContext);

  // Récupérer le nombre d'utilisateurs pour cette équipe
  const team = useTeamDataStandalone({ ownerId: user?.id, userAuth });
  const usersCount = Array.isArray(team.inviteUser) ? team.inviteUser.length : 0;

  // État pour plier/déplier les sections
  const [cabinesOpen, setCabinesOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);

  // Modal overlay pour modification
  const [showModal, setShowModal] = useState(false);
  const [editAction, setEditAction] = useState(null); // null | "name" | "status"

  // Modification du nom
  const [editSubName, setEditSubName] = useState("");

  // Modification du statut
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Double mot de passe pour confirmer le changement de statut
  const [statusPassword, setStatusPassword] = useState("");
  const [statusPasswordConfirm, setStatusPasswordConfirm] = useState("");
  const [statusPasswordError, setStatusPasswordError] = useState("");
  const [showStatusPassword, setShowStatusPassword] = useState(false);
  const [showStatusPasswordConfirm, setShowStatusPasswordConfirm] = useState(false);

  // État pour le chargement
  const [loading, setLoading] = useState(false);

  // Helper pour le label du statut actuel
  const currentStatusLabel = getStatusLabel(sub?.is_active);
  const currentStatusKey = Number(sub?.is_active);

  const handleOpenModal = () => {
    setShowModal(true);
    setEditAction(null);
    setEditSubName(sub?.name || "");
    setSelectedStatus(currentStatusKey);
    setStatusPassword("");
    setStatusPasswordConfirm("");
    setStatusPasswordError("");
  };

  const handleCloseModal = (skipToast = false) => {
    setShowModal(false);
    setEditAction(null);
    setEditSubName("");
    setSelectedStatus(null);
    setStatusPassword("");
    setStatusPasswordConfirm("");
    setStatusPasswordError("");
    if (!loading && !skipToast) {
      toast.info(t("subscription.modification_cancelled"));
    }
  };

  const handleSaveName = async () => {
    if (!editSubName.trim()) return;

    setLoading(true);
    try {
      await Axios.put(`/subscriptions/${sub.id}`, {
        name: editSubName.trim(),
      });

      sub.name = editSubName.trim();
      toast.success(t("subscription.name_updated"));
      handleCloseModal(true);
    } catch (err) {
      console.error("❌ Erreur modification nom abonnement:", err);
      toast.error(t("subscription.error_updating_name"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    if (selectedStatus === null || selectedStatus === currentStatusKey) {
      toast.info(t("subscription.no_status_change"));
      return;
    }
    if (!statusPassword.trim() || !statusPasswordConfirm.trim()) {
      setStatusPasswordError(t("subscription.enter_password_twice"));
      return;
    }
    if (statusPassword !== statusPasswordConfirm) {
      setStatusPasswordError(t("common.passwords_not_match"));
      return;
    }

    setLoading(true);
    setStatusPasswordError("");
    try {
      await Axios.patch(`/subscriptions/${sub.id}/status`, {
        status: selectedStatus,
        adminPassword: statusPassword,
      });

      sub.is_active = selectedStatus;
      toast.success(t("subscription.status_updated", { status: getStatusLabel(selectedStatus) }));
      handleCloseModal(true);
    } catch (err) {
      console.error("❌ Erreur changement statut:", err);
      const errorMessage =
        err.response?.data?.error || t("subscription.error_changing_status");
      setStatusPasswordError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user || !sub) {
    return <div style={{ padding: "20px", textAlign: "center" }}>{t("subscription.none_selected")}</div>;
  }

  return (
    <div className={styles.drawerWrap}>
      {/* Infos abonnement */}
      <div className={styles.drawerSection}>
        <div className={styles.infoRow}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>
              {user?.email}
              <button
                type="button"
                className={`${styles.btnCopyEmail} has-tooltip`}
                aria-label={t("common.copy_email")}
                onClick={() => {
                  navigator.clipboard.writeText(user?.email || "");
                  toast.success(t("subscription.email_copied"));
                }}
              >
                <FiCopy />
              </button>
            </span>
          </div>
          {sub.subid ? (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Odoo</span>
              <span className={styles.infoValue}>{sub.subid}</span>
            </div>
          ) : null}
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Statut</span>
            <div className={styles.statusRow}>
              <span className={
                currentStatusKey === SUBSCRIPTION_STATUSES.ACTIVE ? styles.statusActiveText
                : currentStatusKey === SUBSCRIPTION_STATUSES.PAYMENT_PENDING ? styles.statusWarningText
                : styles.statusInactiveText
              }>
                {currentStatusKey === SUBSCRIPTION_STATUSES.ACTIVE && <><FiCheckCircle /> {currentStatusLabel}</>}
                {currentStatusKey === SUBSCRIPTION_STATUSES.INACTIVE && <><FiXCircle /> {currentStatusLabel}</>}
                {currentStatusKey === SUBSCRIPTION_STATUSES.PAYMENT_PENDING && <><FiAlertTriangle /> {currentStatusLabel}</>}
              </span>
              <button
                type="button"
                className={styles.btnModifier}
                aria-label={t("subscription.modify_subscription")}
                onClick={handleOpenModal}
              >
                <FiEdit2 /> {t("subscription.modify")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal overlay pour modification — couvre tout le drawer */}
      {showModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div className={styles.modalContent}>
              {/* Choix d'action */}
              {editAction === null && (
                <>
                  <h3 className={styles.modalTitle}>{t("subscription.modify_subscription")}</h3>
                  <div className={styles.modalActions}>
                    <button
                      type="button"
                      className={styles.editChoiceCancelBtn}
                      aria-label={t("common.cancel")}
                      onClick={handleCloseModal}
                    >
                      <FiX /> {t("common.cancel")}
                    </button>
                    <button
                      type="button"
                      className={styles.editChoiceBtn}
                      aria-label={t("subscription.modify_name")}
                      onClick={() => setEditAction("name")}
                    >
                      <FiEdit2 /> {t("subscription.modify_name")}
                    </button>
                    <button
                      type="button"
                      className={styles.editChoiceStatusBtn}
                      aria-label={t("subscription.modify_status")}
                      onClick={() => setEditAction("status")}
                    >
                      <FiAlertTriangle /> {t("subscription.modify_status")}
                    </button>
                  </div>
                </>
              )}

              {/* Formulaire de modification du nom */}
              {editAction === "name" && (
                <div className={styles.editNameForm}>
                  <h3 className={styles.modalTitle}>{t("subscription.modify_name")}</h3>
                  <label className={styles.editLabel}>{t("subscription.sub_name_label")}</label>
                  <input
                    type="text"
                    className={styles.editInput}
                    value={editSubName}
                    onChange={(e) => setEditSubName(e.target.value)}
                    placeholder={t("subscription.sub_name_label")}
                    autoFocus
                  />
                  <div className={styles.editFormButtons}>
                    <button
                      type="button"
                      className={styles.btnCancelEdit}
                      aria-label={t("common.cancel")}
                      onClick={handleCloseModal}
                      disabled={loading}
                    >
                      <FiX /> {t("common.cancel")}
                    </button>
                    <button
                      type="button"
                      className={styles.btnConfirmEdit}
                      aria-label={t("common.save")}
                      onClick={handleSaveName}
                      disabled={!editSubName.trim() || loading}
                    >
                      <FiCheck /> {t("common.save")}
                    </button>
                  </div>
                </div>
              )}

              {/* Formulaire de changement de statut */}
              {editAction === "status" && (
                <div className={styles.statusChangeForm}>
                  <h3 className={styles.modalTitle}>{t("subscription.modify_status")}</h3>
                  <p className={styles.statusCurrentLabel}>
                    {t("subscription.current_status")} <b>{currentStatusLabel}</b>
                  </p>
                  <div className={styles.statusOptions}>
                    {getStatusOptions().map(({ key, value, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.statusOptionBtn} ${
                          value === selectedStatus ? styles.statusOptionSelected : ""
                        } ${
                          value === SUBSCRIPTION_STATUSES.ACTIVE ? styles.statusOptionActive
                          : value === SUBSCRIPTION_STATUSES.INACTIVE ? styles.statusOptionInactive
                          : styles.statusOptionWarning
                        }`}
                        onClick={() => setSelectedStatus(value)}
                      >
                        {value === SUBSCRIPTION_STATUSES.ACTIVE && <FiCheckCircle />}
                        {value === SUBSCRIPTION_STATUSES.INACTIVE && <FiXCircle />}
                        {value === SUBSCRIPTION_STATUSES.PAYMENT_PENDING && <FiAlertTriangle />}
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className={styles.statusPasswordSection}>
                    <div className={styles.passwordInputWrap}>
                      <input
                        type={showStatusPassword ? "text" : "password"}
                        className={styles.statusInput}
                        placeholder={t("subscription.admin_password")}
                        value={statusPassword}
                        onChange={(e) => setStatusPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className={`${styles.passwordToggleBtn} has-tooltip`}
                        onClick={() => setShowStatusPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={showStatusPassword ? t("common.hide") : t("common.show")}
                      >
                        {showStatusPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    <div className={styles.passwordInputWrap}>
                      <input
                        type={showStatusPasswordConfirm ? "text" : "password"}
                        className={styles.statusInput}
                        placeholder={t("common.password_confirm")}
                        value={statusPasswordConfirm}
                        onChange={(e) => setStatusPasswordConfirm(e.target.value)}
                      />
                      <button
                        type="button"
                        className={`${styles.passwordToggleBtn} has-tooltip`}
                        onClick={() => setShowStatusPasswordConfirm((v) => !v)}
                        tabIndex={-1}
                        aria-label={showStatusPasswordConfirm ? t("common.hide") : t("common.show")}
                      >
                        {showStatusPasswordConfirm ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {statusPasswordError && (
                      <span className={styles.statusError}>{statusPasswordError}</span>
                    )}
                  </div>
                  <div className={styles.editFormButtons}>
                    <button
                      type="button"
                      className={styles.btnCancelEdit}
                      aria-label={t("common.cancel")}
                      onClick={handleCloseModal}
                      disabled={loading}
                    >
                      <FiX /> {t("common.cancel")}
                    </button>
                    <button
                      type="button"
                      className={styles.btnConfirmEdit}
                      aria-label={t("common.confirm")}
                      onClick={handleSaveStatus}
                      disabled={selectedStatus === currentStatusKey || !statusPassword.trim() || !statusPasswordConfirm.trim() || loading}
                    >
                      <FiCheck /> {t("common.confirm")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Section Cabines (pliable, affichée en premier) */}
      <div className={styles.drawerSection}>
        <button
          type="button"
          className={styles.collapsibleHeader}
          onClick={() => setCabinesOpen((prev) => !prev)}
        >
          <span className={styles.drawerSectionTitle}>{t("subscription.cabines_section")} ({(sub.cabines || []).length})</span>
          {cabinesOpen ? <FiChevronUp /> : <FiChevronDown />}
        </button>
        {cabinesOpen && (
          <div className={styles.collapsibleContent}>
            <CabinesSection sub={sub} user={user} onMoved={onRefresh} />
          </div>
        )}
      </div>

      {/* Section Utilisateurs / Équipe (pliable, affichée en second) */}
      {user?.id && (
        <div className={styles.drawerSection}>
          <button
            type="button"
            className={styles.collapsibleHeader}
            onClick={() => setUsersOpen((prev) => !prev)}
          >
            <span className={styles.drawerSectionTitle}>
              {t("subscription.team_management_section", { count: usersCount })}
            </span>
            {usersOpen ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          {usersOpen && (
            <div
              className={styles.collapsibleContent}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <OwnerTeamPanelWithData ownerId={user.id} ownerEmail={user.email} userAuth={userAuth} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SubscriptionDrawerPanel;